import { useRef, useState, useCallback, useEffect } from 'react';
import { useAppStore } from '@/lib/store';
import { RealtimePose } from '@/lib/realtime-pose';
import { AngleDetector, TARGET_ANGLES } from '@/lib/angle-detector';
import { captureKeyframe, MIN_KEYFRAMES } from '@/lib/rotation-processor';
import { useVoiceGuide } from '@/lib/use-voice-guide';
import { useBeep } from '@/lib/use-beep';
import { RotationOverlay } from './RotationOverlay';
import type { PoseFrame } from '@/lib/realtime-pose';
import type { RotationKeyframe } from '@/types/measurements';

type CapturePhase = 'setup' | 'countdown' | 'rotating' | 'complete';

const COUNTDOWN_DURATION = 3;

/** Degrees per second threshold for "slow down" warning. */
const MAX_ROTATION_SPEED = 60;
/** Seconds stalled before "keep turning" prompt. */
const STALL_THRESHOLD_SEC = 3;

export function RotationCapture() {
  const {
    frontDetection,
    addRotationKeyframe,
    clearRotationKeyframes,
    setStep,
  } = useAppStore();

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const poseRef = useRef<RealtimePose | null>(null);
  const angleDetectorRef = useRef<AngleDetector | null>(null);
  const lastAngleRef = useRef<{ angle: number; time: number } | null>(null);
  const lastCaptureTimeRef = useRef<number>(0);

  const [phase, setPhase] = useState<CapturePhase>('setup');
  const [cameraStarted, setCameraStarted] = useState(false);
  const [poseFrame, setPoseFrame] = useState<PoseFrame | null>(null);
  const [modelLoading, setModelLoading] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [currentAngle, setCurrentAngle] = useState(0);
  const [capturedAngles, setCapturedAngles] = useState<Set<number>>(new Set());
  const [keyframes, setKeyframes] = useState<RotationKeyframe[]>([]);

  const { playBeep } = useBeep();
  const voiceGuide = useVoiceGuide();
  const [voiceEnabled, setVoiceEnabled] = useState(true);

  // Initialize angle detector with reference shoulder span from front photo
  useEffect(() => {
    if (frontDetection?.landmarks) {
      const lShoulder = frontDetection.landmarks[11];
      const rShoulder = frontDetection.landmarks[12];
      if (lShoulder && rShoulder) {
        const span = Math.abs(rShoulder.x - lShoulder.x);
        angleDetectorRef.current = new AngleDetector(span);
      }
    }
    if (!angleDetectorRef.current) {
      angleDetectorRef.current = new AngleDetector();
    }
  }, [frontDetection]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      poseRef.current?.dispose();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      voiceGuide.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track angle from pose frames during rotation
  useEffect(() => {
    if (phase !== 'rotating' || !poseFrame || !angleDetectorRef.current) return;

    const estimate = angleDetectorRef.current.estimate(poseFrame.landmarks);
    if (!estimate) return;

    setCurrentAngle(estimate.angle);

    // Speed monitoring
    const now = Date.now();
    if (lastAngleRef.current && voiceEnabled) {
      const dt = (now - lastAngleRef.current.time) / 1000;
      if (dt > 0.2) {
        const angleDelta = Math.abs(estimate.angle - lastAngleRef.current.angle);
        // Handle wraparound (e.g. 350 -> 10 = 20 degrees, not 340)
        const wrappedDelta = Math.min(angleDelta, 360 - angleDelta);
        const speed = wrappedDelta / dt;

        if (speed > MAX_ROTATION_SPEED) {
          voiceGuide.speak('Easy now, slow it down!');
        }
      }

      // Stall detection: no new capture for STALL_THRESHOLD_SEC
      const timeSinceCapture = (now - lastCaptureTimeRef.current) / 1000;
      if (lastCaptureTimeRef.current > 0 && timeSinceCapture > STALL_THRESHOLD_SEC && capturedAngles.size < TARGET_ANGLES.length - 1) {
        const remaining = TARGET_ANGLES.length - capturedAngles.size;
        voiceGuide.speak(`You've got ${capturedAngles.size} angles, ${remaining} to go. Keep turning!`);
        lastCaptureTimeRef.current = now; // Reset so we don't spam
      }
    }
    lastAngleRef.current = { angle: estimate.angle, time: now };

    // Check if any target angle is reached
    for (const target of TARGET_ANGLES) {
      if (capturedAngles.has(target)) continue;
      if (angleDetectorRef.current.isTargetReached(target)) {
        // Capture keyframe
        const video = videoRef.current;
        if (!video) break;

        const kf = captureKeyframe(video, target, poseFrame.landmarks);
        addRotationKeyframe(kf);
        setKeyframes((prev) => [...prev, kf]);
        setCapturedAngles((prev) => new Set([...prev, target]));
        lastCaptureTimeRef.current = Date.now();
        playBeep(1000, 80);

        // Voice milestones with progress counts
        if (voiceEnabled) {
          const captured = capturedAngles.size + 1;
          const remaining = TARGET_ANGLES.length - captured;
          if (remaining <= 0) {
            voiceGuide.speak('Full rotation! You nailed it, champion!');
          } else if (captured === 1) {
            voiceGuide.speak(`First angle down! ${remaining} to go, keep spinning!`);
          } else if (captured === Math.floor(TARGET_ANGLES.length / 2)) {
            voiceGuide.speak(`Halfway! ${remaining} to go, you're killing it!`);
          } else if (remaining <= 2) {
            voiceGuide.speak(`Almost there! Just ${remaining} more!`);
          } else if (captured % 3 === 0) {
            voiceGuide.speak(`That's ${captured} captured, ${remaining} to go!`);
          }
        }

        break; // Only capture one target per frame
      }
    }

    // Check completion
    if (capturedAngles.size >= TARGET_ANGLES.length - 1) {
      // All or nearly all targets captured
      handleComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poseFrame, phase]);

  const stopCamera = useCallback(() => {
    poseRef.current?.stop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraStarted(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1080 },
          height: { ideal: 1920 },
        },
        audio: false,
      });

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        setCameraError('Camera element not ready. Please try again.');
        return;
      }

      video.srcObject = stream;
      await new Promise<void>((resolve) => {
        if (video.readyState >= 1) { resolve(); return; }
        video.addEventListener('loadedmetadata', () => resolve(), { once: true });
      });
      await video.play();
      setCameraStarted(true);

      // Start pose detection
      if (!poseRef.current) {
        poseRef.current = new RealtimePose((frame) => setPoseFrame(frame));
      }
      if (!poseRef.current.isReady && !poseRef.current.isLoading) {
        setModelLoading(true);
        await poseRef.current.load();
        setModelLoading(false);
      }
      poseRef.current.start(video);
      setPhase('setup');

      // Fullscreen
      try {
        await containerRef.current?.requestFullscreen?.();
      } catch {
        // Fine
      }
    } catch {
      setCameraError('Could not start camera. Please check permissions.');
    }
  }, []);

  function startCountdown() {
    setPhase('countdown');
    let tick = COUNTDOWN_DURATION;
    setCountdown(tick);
    if (voiceEnabled) voiceGuide.speak('Let\'s do this! Face the camera and get ready to spin!', 'capture');

    const timer = setInterval(() => {
      tick -= 1;
      if (tick <= 0) {
        clearInterval(timer);
        setCountdown(0);
        setPhase('rotating');
        clearRotationKeyframes();
        setCapturedAngles(new Set());
        setKeyframes([]);
        angleDetectorRef.current?.reset();
        lastAngleRef.current = null;
        lastCaptureTimeRef.current = Date.now();
        if (voiceEnabled) voiceGuide.speak('Now turn slowly like a supermodel! Nice and easy!', 'capture');
      } else {
        setCountdown(tick);
        playBeep(800, 100);
      }
    }, 1000);
  }

  function handleComplete() {
    setPhase('complete');
    stopCamera();
    playBeep(1200, 300);
    if (voiceEnabled) voiceGuide.speak('Awesome! Let me crunch those numbers for you!');

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }

    // Navigate to processing after a short delay
    setTimeout(() => {
      setStep('processing_rotation');
    }, 1500);
  }

  function handleCancel() {
    stopCamera();
    clearRotationKeyframes();
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setStep('side_prompt');
  }

  function handleFinishEarly() {
    if (keyframes.length >= MIN_KEYFRAMES) {
      handleComplete();
    }
  }

  // Always render the video element so startCamera can attach to it.
  // Show setup screen as overlay when camera hasn't started yet.
  return (
    <div ref={containerRef} className={cameraStarted ? 'camera-container' : 'relative min-h-screen'}>
      {/* Video element — always in DOM so startCamera() can reference it */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cameraStarted ? 'camera-feed' : 'hidden'}
      />

      {/* ── Setup screen (before camera starts) ── */}
      {!cameraStarted && phase === 'setup' && (
        <div className="screen items-center justify-center text-center">
          <h2 className="text-2xl font-bold text-gray-900">Precision Scan</h2>
          <p className="mt-2 text-sm text-gray-500">
            Prop your phone on a surface at waist height, 2-3 meters away.
            You&apos;ll slowly rotate 360° while the app captures your body from all angles.
          </p>

          <div className="mt-6 space-y-2">
            <div className="card text-left">
              <h3 className="text-sm font-semibold text-gray-900">Setup instructions:</h3>
              <ul className="mt-2 space-y-1 text-sm text-gray-500">
                <li>1. Place phone on a stable surface (table, shelf)</li>
                <li>2. Phone at waist height, 2-3m away</li>
                <li>3. Use rear camera for best quality</li>
                <li>4. Good lighting, clear space to rotate</li>
              </ul>
            </div>
          </div>

          {cameraError && (
            <p className="mt-4 text-sm text-red-500">{cameraError}</p>
          )}

          <div className="mt-6 w-full space-y-3">
            <button onClick={startCamera} className="btn-primary">
              Start Camera
            </button>
            <button onClick={handleCancel} className="btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      )}

      {/* ── Camera active: overlays ── */}
      {cameraStarted && (
        <>
          {/* Rotation overlay */}
          <RotationOverlay
            phase={phase}
            currentAngle={currentAngle}
            capturedAngles={capturedAngles}
            countdown={countdown}
            modelLoading={modelLoading}
            keyframeCount={keyframes.length}
            totalTargets={TARGET_ANGLES.length}
          />

          {/* Top hint */}
          {phase === 'setup' && (
            <div
              className="absolute left-0 right-0 top-0 z-[3] bg-gradient-to-b from-black/60 to-transparent px-4 pb-8 pt-4"
              style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
            >
              <p className="text-center text-sm font-medium text-white/90">
                {poseFrame?.alignment.allAligned
                  ? 'Tap Start when ready!'
                  : 'Position yourself in frame, then tap Start'}
              </p>
            </div>
          )}

          {phase === 'rotating' && (
            <div
              className="absolute left-0 right-0 top-0 z-[3] bg-gradient-to-b from-black/60 to-transparent px-4 pb-8 pt-4"
              style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
            >
              <p className="text-center text-sm font-medium text-white/90">
                Turn slowly... {keyframes.length}/{TARGET_ANGLES.length} angles captured
              </p>
            </div>
          )}

          {/* Bottom controls */}
          <div className="camera-controls">
            {phase === 'setup' && (
              <div className="flex w-full items-center justify-center gap-4">
                {/* Voice toggle */}
                <button
                  onClick={() => setVoiceEnabled(!voiceEnabled)}
                  className={`rounded-full px-3 py-2 text-xs font-medium backdrop-blur-sm ${
                    voiceEnabled ? 'bg-brand-500/80 text-white' : 'bg-black/40 text-white/70'
                  }`}
                >
                  Voice {voiceEnabled ? 'On' : 'Off'}
                </button>

                {/* Start button */}
                <button
                  onClick={startCountdown}
                  className="rounded-full bg-brand-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg active:scale-95 transition-transform"
                >
                  Start Scan
                </button>

                {/* Cancel */}
                <button
                  onClick={handleCancel}
                  className="rounded-full bg-black/50 p-3 text-white backdrop-blur-sm"
                  aria-label="Cancel"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {phase === 'rotating' && (
              <div className="flex w-full flex-col items-center gap-3">
                {/* Primary: Finish / Process button — always visible */}
                <button
                  onClick={handleFinishEarly}
                  disabled={keyframes.length < MIN_KEYFRAMES}
                  className={`rounded-full px-8 py-4 text-base font-bold shadow-lg transition-all ${
                    keyframes.length >= MIN_KEYFRAMES
                      ? 'bg-green-500 text-white active:scale-95 animate-pulse'
                      : 'bg-white/20 text-white/50 cursor-not-allowed'
                  }`}
                >
                  {keyframes.length >= MIN_KEYFRAMES
                    ? `Process with ${keyframes.length} angles`
                    : `Need ${MIN_KEYFRAMES - keyframes.length} more angles`}
                </button>
                <button
                  onClick={handleCancel}
                  className="rounded-full bg-red-500/80 px-5 py-2 text-sm font-semibold text-white backdrop-blur-sm"
                >
                  Cancel
                </button>
              </div>
            )}

            {phase === 'complete' && (
              <div className="flex w-full items-center justify-center">
                <div className="rounded-full bg-green-500/90 px-8 py-3 text-base font-semibold text-white backdrop-blur-sm">
                  {keyframes.length} angles captured!
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
